import { apiClient } from './api';

export interface CartItemResponse {
  id: string;
  productId: string;
  productName: string;
  productSlug?: string | null;
  sellerId?: string | null;
  sellerProductId?: string | null;
  sellerName?: string | null;
  sellerRating?: number | null;
  sellerLogoUrl?: string | null;
  sellerBadges?: string[] | null;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  currency?: string | null;
}

export interface CartTotalsResponse {
  subtotal: number;
  totalQuantity: number;
}

export interface CartResponse {
  id: string;
  status: string;
  currency?: string | null;
  updatedAt?: string | null;
  totals: CartTotalsResponse;
  items: CartItemResponse[];
}

export interface UpsertCartItemRequest {
  productId: string;
  quantity: number;
  sellerProductId?: string;
  sellerId?: string;
}

export interface UpdateCartItemRequest {
  productId: string;
  quantity: number;
  sellerProductId?: string;
  sellerId?: string;
}

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normaliseStringArray = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  const normalized = value
    .map(entry => (typeof entry === 'string' ? entry.trim() : null))
    .filter((entry): entry is string => Boolean(entry && entry.length > 0));

  return normalized;
};

const pruneUndefined = <T extends object>(data: T): T =>
  Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined)
  ) as T;

type CartRecord = Record<string, unknown>;

const asCartRecord = (value: unknown): CartRecord =>
  value && typeof value === 'object' ? (value as CartRecord) : {};

const normaliseCartItem = (item: unknown): CartItemResponse => {
  const source = asCartRecord(item);
  return {
    id: String(source.id ?? ''),
    productId: String(source.productId ?? ''),
    productName: typeof source.productName === 'string' ? source.productName : typeof source.name === 'string' ? source.name : '',
    productSlug: typeof source.productSlug === 'string' ? source.productSlug : null,
    sellerId: source.sellerId ? String(source.sellerId) : null,
    sellerProductId: source.sellerProductId ? String(source.sellerProductId) : null,
    sellerName: typeof source.sellerName === 'string' ? source.sellerName : null,
    sellerRating: toNullableNumber(source.sellerRating),
    sellerLogoUrl: typeof source.sellerLogoUrl === 'string' ? source.sellerLogoUrl : null,
    sellerBadges: normaliseStringArray(source.sellerBadges),
    quantity: Number(source.quantity ?? 0),
    unitPrice: toNumber(source.unitPrice ?? source.price, 0),
    lineTotal: toNumber(source.lineTotal ?? source.subtotal, 0),
    currency: typeof source.currency === 'string' ? source.currency : null
  };
};

const normaliseCartResponse = (response: unknown): CartResponse => {
  const source = asCartRecord(response);
  const totals = asCartRecord(source.totals);
  const items = Array.isArray(source.items) ? source.items : [];

  return {
    id: String(source.id ?? ''),
    status: typeof source.status === 'string' ? source.status : 'ACTIVE',
    currency: typeof source.currency === 'string' ? source.currency : null,
    updatedAt: typeof source.updatedAt === 'string' ? source.updatedAt : null,
    totals: {
      subtotal: toNumber(totals.subtotal ?? source.total, 0),
      totalQuantity: Number(
        totals.totalQuantity ??
          items.reduce(
            (sum: number, item: unknown) => sum + Number(asCartRecord(item).quantity ?? 0),
            0
          )
      )
    },
    items: items.map(normaliseCartItem)
  };
};

export const cartApi = {
  /**
   * Get current user's cart
   */
  getCart: async (): Promise<CartResponse> => {
    const raw = await apiClient.get<unknown>('/v2/cart', true);
    return normaliseCartResponse(raw);
  },

  /**
   * Add or update an item in the cart
   */
  addToCart: async (data: UpsertCartItemRequest): Promise<CartResponse> => {
    const raw = await apiClient.post<unknown>('/v2/cart/items', pruneUndefined(data), true);
    return normaliseCartResponse(raw);
  },

  /**
   * Update cart item quantity by cart item identifier
   */
  updateCartItem: async (data: UpdateCartItemRequest): Promise<CartResponse> => {
    const raw = await apiClient.post<unknown>('/v2/cart/items', pruneUndefined(data), true);
    return normaliseCartResponse(raw);
  },

  /**
   * Remove item from cart by product identifier
   */
  removeFromCart: async (
    productId: string,
    options?: { sellerProductId?: string | null }
  ): Promise<CartResponse> => {
    const query = new URLSearchParams();
    if (options?.sellerProductId) {
      query.set('sellerProductId', options.sellerProductId);
    }

    const endpoint = query.toString()
      ? `/v2/cart/items/${encodeURIComponent(productId)}?${query.toString()}`
      : `/v2/cart/items/${encodeURIComponent(productId)}`;
    const raw = await apiClient.delete<unknown>(endpoint, true);
    return normaliseCartResponse(raw);
  },

  /**
   * Clear entire cart
   */
  clearCart: async (): Promise<CartResponse> => {
    const raw = await apiClient.delete<unknown>('/v2/cart', true);
    return normaliseCartResponse(raw);
  },
};
